/**
 * Beta Update Banner
 * Shows a dismissable notification when a beta firmware update is available.
 * User must explicitly consent to install beta updates.
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Beaker,
  X,
  Download,
  Loader2,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { useBetaUpdateCheck, useTriggerUpdate } from '../../hooks/useOTA';
import { Button } from '../UI/Button';

interface BetaUpdateBannerProps {
  deviceId: string;
  currentVersion: string;
}

export const BetaUpdateBanner: React.FC<BetaUpdateBannerProps> = ({
  deviceId,
  currentVersion,
}) => {
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: betaCheck } = useBetaUpdateCheck(deviceId, currentVersion);
  const triggerMutation = useTriggerUpdate();

  // No beta update or user dismissed this version
  if (
    !betaCheck?.update_available ||
    !betaCheck.latest_version ||
    dismissed === betaCheck.latest_version
  ) {
    return null;
  }

  const handleInstall = async () => {
    if (!betaCheck.latest_version) return;
    try {
      await triggerMutation.mutateAsync({
        deviceId,
        version: betaCheck.latest_version,
      });
      setConfirmOpen(false);
    } catch {
      // error shown in UI
    }
  };

  return (
    <>
      {/* Banner */}
      <AnimatePresence>
        {!confirmOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-teal-50 dark:bg-teal-950/40 border-b border-teal-200 dark:border-teal-800"
          >
            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <Beaker className="w-4 h-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                <p className="text-sm text-teal-800 dark:text-teal-200 truncate">
                  <strong>Beta v{betaCheck.latest_version}</strong> je k dispozici
                  {betaCheck.changelog && (
                    <span className="hidden sm:inline text-teal-600 dark:text-teal-400">
                      {' '}— {betaCheck.changelog}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmOpen(true)}
                  className="text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/50"
                >
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Instalovat
                </Button>
                <button
                  onClick={() => setDismissed(betaCheck.latest_version!)}
                  className="p-1 rounded hover:bg-teal-100 dark:hover:bg-teal-900/50 text-teal-500"
                  title="Skrýt nabídku beta aktualizace"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {confirmOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50"
              onClick={() => setConfirmOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative z-10 w-full max-w-md bg-white dark:bg-zinc-800 rounded-xl shadow-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30">
                  <Beaker className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Instalovat beta verzi?
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-zinc-400">
                    v{betaCheck.latest_version}
                  </p>
                </div>
              </div>

              <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    Beta verze mohou obsahovat chyby a nestabilní funkce.
                    Zařízení se po instalaci automaticky restartuje.
                  </p>
                </div>
              </div>

              {betaCheck.changelog && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-zinc-900/50 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 dark:text-zinc-400 mb-1">
                    Changelog:
                  </p>
                  <p className="text-sm text-gray-700 dark:text-zinc-300">
                    {betaCheck.changelog}
                  </p>
                </div>
              )}

              <div className="text-xs text-gray-500 dark:text-zinc-400 mb-4">
                Zařízení: <code className="bg-gray-100 dark:bg-zinc-700 px-1 rounded">{deviceId}</code>
                {betaCheck.file_size && (
                  <> · Velikost: {(betaCheck.file_size / 1024 / 1024).toFixed(2)} MB</>
                )}
              </div>

              {triggerMutation.isError && (
                <div className="flex items-center gap-2 text-red-600 text-sm mb-4">
                  <AlertTriangle className="w-4 h-4" />
                  {(triggerMutation.error as Error)?.message || 'Chyba při spuštění OTA'}
                </div>
              )}

              {triggerMutation.isSuccess && (
                <div className="flex items-center gap-2 text-green-600 text-sm mb-4">
                  <CheckCircle className="w-4 h-4" />
                  OTA aktualizace spuštěna! Zařízení se brzy restartuje.
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setConfirmOpen(false);
                    setDismissed(betaCheck.latest_version!);
                  }}
                >
                  Ne, děkuji
                </Button>
                <Button
                  onClick={handleInstall}
                  disabled={triggerMutation.isPending || triggerMutation.isSuccess}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {triggerMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Instaluji...
                    </>
                  ) : triggerMutation.isSuccess ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Spuštěno
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Ano, instalovat beta
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
